import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Chart, Legend, Interval, Coord } from "bizcharts"

import ErrorBoundary from "../../../../../../components/ErrorBoundary"

function getTickInterval(granularity) {
  switch (granularity) {
    case "Hours":
      return 60 * 60 * 1000
    case "Days":
      return 24 * 60 * 60 * 1000
    case "Minutes":
      return 60 * 1000
    case "Seconds":
      return 1000
    default:
      return 24 * 60 * 60 * 1000
  }
}

function getMask(granularity) {
  switch (granularity) {
    case "Seconds":
      return "DD MMM HH:mm:ss"
    case "Minutes":
      return "DD MMM HH:mm"
    case "Hours":
      return "DD MMM HH:mm"
    case "Days":
      return "DD MMM"
    case "Months":
      return "MMM YYYY"
    case "Years":
      return "YYYY"
    default:
      return "DD MMM"
  }
}

const BarChartWithLegend = ({ graph = {}, theme }) => {

  const legendColors = useMemo(() => {
    if (process.env.REACT_APP_GRAPH_COLOR) {
      return ["type", process.env.REACT_APP_GRAPH_COLOR.split(",")]
    } else {
      return ["type"]
    }
  }, [])

  const xAxisDetails = graph?.graphMetadata?.xAxisDetails || {}
  const chartType = graph?.graphMetadata?.chartType || "normal"
  const orientation = graph?.graphMetadata?.orientation
  const hasXAxisDetails = !!graph?.graphMetadata?.xAxisDetails
  const mask = getMask(xAxisDetails.granularity)
  const tickInterval = getTickInterval(xAxisDetails.granularity)

  const isHorizontal = orientation === "horizontal"

  const originalData = graph?.graphData || []
  const chartData = useMemo(() => {
    if (chartType === "grouped" && originalData.length > 0) {
      return originalData.map(item => {
        const date = new Date(item.xAxisValue)
        let formattedDate

        switch (xAxisDetails.granularity) {
          case "Seconds":
            formattedDate = date.toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })
            break
          case "Minutes":
            formattedDate = date.toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
            break
          case "Hours":
            formattedDate = date.toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
            break
          case "Days":
            formattedDate = date.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            })
            break
          case "Weeks":
            formattedDate = date.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            })
            break
          case "Months":
            formattedDate = date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            })
            break
          default:
            formattedDate = date.toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
            })
        }

        return {
          ...item,
          xAxisValue: formattedDate,
        }
      })
    }
    return originalData
  }, [chartType, originalData, xAxisDetails.granularity])

  // Determine the adjust configuration based on chart type
  const adjustConfig = useMemo(() => {
    switch (chartType) {
      case "normal":
        return [] // No adjustment for normal bar chart
      case "grouped":
        return [{ type: "dodge", marginRatio: -0.3 }] // Grouped bars without gaps
      case "stacked":
      default:
        return [{ type: "stack" }] // Stacked bars (default)
    }
  }, [chartType])

  const scale = useMemo(() => {
    const base = { yAxisValue: { min: 0, nice: true } }
    if (hasXAxisDetails) {
      const xAxisType =
        chartType === "grouped" ? "cat" : xAxisDetails.type || "time"
      return {
        ...base,
        xAxisValue: {
          type: xAxisType,
          ...(xAxisType === "time" && {
            tickInterval,
            mask,
            range:  chartType === "normal" ? [0.03, 1] : undefined,
          }),
        },
      }
    }
    return base
  }, [hasXAxisDetails, xAxisDetails.type, chartType, tickInterval, mask])

  return (
    <ErrorBoundary>
      <Chart
        theme={theme}
        data={chartData}
        padding={isHorizontal ? [60, 40, 40, 60] : [40, 40, 40, 40]} 
        height={350}
        scale={scale}
        animate={false}
        autoFit
      >
        <Legend position="top" />
        <Interval
          position="xAxisValue*yAxisValue"
          color={legendColors}
          adjust={adjustConfig}
          size={isHorizontal ? 15 : chartType === "grouped" ? 10 : undefined} 
        />
        {isHorizontal && <Coord transpose />}
      </Chart>
    </ErrorBoundary>
  )
}

const mapStateToProps = state => ({
  theme: state.page_details.theme,
})

BarChartWithLegend.propTypes = {
  graph: PropTypes.object,
  theme: PropTypes.string,
}

BarChartWithLegend.defaultProps = {
  graph: {},
  theme: "light",
}

export default connect(mapStateToProps)(BarChartWithLegend)
